import React, { useState, useEffect } from "react";
import GenericPopup from "@Components/Popup/Popup";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import { Box } from "@mui/system";
import Joi from "joi";
import {
  OptionalDropDownValidation,
  pastOrNowDateValidation,
} from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import DropDown from "../../../Components/DropDown/DropDown";
import { validateForm } from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import {
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import DateSelector from "@Components/DateTimePickers/DatePicker";
import UploadFileButton from "../../../Components/Buttons/uploadFileButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LockIcon from "@mui/icons-material/Lock";
import CancelIcon from "@mui/icons-material/Cancel";
import { red } from "@mui/material/colors";

export const AgentDocumentPopup = ({
  showPopup,
  closePopup,
  Confirm,
  loading,
  initialDocument,
  isEditing = false,
  entityLabel = "Agent",
}) => {
  const emptyDocument = {
    documentName: "Select Document Name",
    startingDate: null,
    expiryDate: null,
    emails: [],
  };

  const [document, setDocument] = useState(emptyDocument);
  const [file, setFile] = useState();
  const [FileError, setFileError] = useState("");
  const [fileName, setFileName] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (!showPopup) return;
    if (!initialDocument) {
      setDocument({ ...emptyDocument });
      return;
    }
    setDocument({
      documentName: String(initialDocument.documentName || "")
        .replace(/ /g, "_"),
      startingDate: initialDocument.startingDate || null,
      expiryDate: initialDocument.expiryDate || null,
      emails: Array.isArray(initialDocument.emails) ? initialDocument.emails : [],
    });
    setFile(null);
    setFileName("");
    setFileError("");
  }, [showPopup, initialDocument]);

  const addEmail = () => {
    const email = emailInput.trim().replace(/,$/, "");
    if (!email) return;

    const { error } = Joi.string()
      .email({ tlds: { allow: false } })
      .validate(email);
    if (error) {
      setEmailError("Invalid Email");
      return;
    }

    if (!document.emails.includes(email)) {
      setDocument((prev) => ({ ...prev, emails: [...prev.emails, email] }));
    }
    setEmailInput("");
    setEmailError("");
  };

  const validationSchema = Joi.object({
    documentName: OptionalDropDownValidation("Document Name"),
    emails: Joi.array()
      .items(Joi.string().email({ tlds: { allow: false } }))
      .required(),
    startingDate: Joi.date().required().messages({
      "date.base": "Starting Date is required",
      "any.required": "Starting Date is required",
    }),
    expiryDate: Joi.date().required().messages({
      "date.base": "Expiry Date is required",
      "any.required": "Expiry Date is required",
    }),
  });

  const [errors, setErrors] = useState({
    documentName: "",
    emails: "",
    startingDate: "",
    expiryDate: "",
  });

  const documentNames = [
    { id: "Select Document Name", name: "Select Document Name" },
    { id: "Commercial_Register", name: "Commercial Register" },
    { id: "Tax_Card", name: "Tax Card" },
    { id: "Contract", name: "Contract" },
    { id: "Procuration", name: "Procuration" },
  ];

  const onDatePick = (date, name) => {
    setDocument({ ...document, [name]: date });
    setErrors({ ...errors, [name]: "" }); // Clear error message on date selection
  };

  const onCancel = () => {
    setDocument({ ...emptyDocument });
    setFile(null);
    setFileName("");
    setEmailInput("");
    setEmailError("");
    closePopup();
    setErrors({
      documentName: "",
      emails: "",
      startingDate: "",
      expiryDate: "",
    });
  };
  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) {
      setFileError(!file ? "File Is Required." : "");
      return;
    }
    setFile(uploadedFile);
    setFileName(uploadedFile.name);
    setFileError("");
  };

  const content = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Document Name
        </Typography>
        <DropDown
          state={document.documentName}
          setState={(value) => {
            setDocument({ ...document, documentName: value });
            setErrors({ ...errors, documentName: "" }); // Clear error message on selection
          }}
          name="document Name"
          label=""
          items={documentNames}
          placeholder="Select Document Name"
          helperText={errors.documentName}
        />
      </Grid>

      <Grid item xs={12}>
        <DateSelector
          name={"Starting Date"}
          value={document.startingDate}
          error={errors.startingDate}
          onPick={(date) => {
            setDocument({ ...document, startingDate: date });
            setErrors({ ...errors, startingDate: "" });
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Emails
        </Typography>
        <TextField
          variant="outlined"
          fullWidth
          name="emails"
          placeholder="Enter an email and press Enter"
          value={emailInput}
          onChange={(event) => {
            setEmailInput(event.target.value);
            if (emailError) setEmailError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              addEmail();
            }
          }}
          onBlur={addEmail}
          error={!!emailError}
          helperText={emailError}
        />
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {document.emails.map((email) => (
            <Chip
              key={email}
              label={email}
              size="small"
              icon={<CancelIcon />}
              onClick={() =>
                setDocument((prev) => ({
                  ...prev,
                  emails: prev.emails.filter((item) => item !== email),
                }))
              }
              sx={{
                fontWeight: 520,
                cursor: "pointer",
                "& .MuiChip-icon": { color: red[600] },
              }}
            />
          ))}
        </Box>
      </Grid>
      <Grid item xs={12}>
        <DateSelector
          name={"Expiry Date"}
          value={document.expiryDate}
          error={errors.expiryDate}
          onPick={(date) => {
            setDocument({ ...document, expiryDate: date });
            setErrors({ ...errors, expiryDate: "" });
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <UploadFileButton
          handleFileChange={handleFileChange}
          fileName={fileName}
          FileError={FileError}
        />
        {isEditing && !file && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            A document is already uploaded. Upload a new PDF only if you want to
            replace it.
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  const submit = async () => {
    const pendingEmail = emailInput.trim().replace(/,$/, "");
    let submittedEmails = [...document.emails];
    let pendingEmailError = "";
    if (pendingEmail) {
      const { error } = Joi.string()
        .email({ tlds: { allow: false } })
        .validate(pendingEmail);
      if (error) {
        pendingEmailError = "Invalid Email";
      } else if (!submittedEmails.includes(pendingEmail)) {
        submittedEmails.push(pendingEmail);
      }
    }
    const formattedDocument = {
      ...document,
      emails: submittedEmails,
      startingDate: document.startingDate
        ? typeof document.startingDate.toDate === "function"
          ? document.startingDate.toDate()
          : document.startingDate instanceof Date
          ? document.startingDate
          : new Date(document.startingDate)
        : null,
      expiryDate: document.expiryDate
        ? typeof document.expiryDate.toDate === "function"
          ? document.expiryDate.toDate()
          : document.expiryDate instanceof Date
          ? document.expiryDate
          : new Date(document.expiryDate)
        : null,
    };

    // Reset errors first
    let tempErrors = {
      documentName: "",
      emails: "",
      startingDate: "",
      expiryDate: "",
    };

    // Validate file
    let hasError = false;
    if (!file && !isEditing) {
      setFileError("File Is Required.");
      hasError = true;
    } else {
      setFileError("");
    }

    // Validate any email still being entered and require it to be committed.
    if (pendingEmail) {
      setEmailError(pendingEmailError);
      hasError = !!pendingEmailError;
    }

    // Validate dates
    if (formattedDocument.startingDate && formattedDocument.expiryDate) {
      // Convert to timestamp for reliable comparison
      const startTimestamp = formattedDocument.startingDate.getTime();
      const expiryTimestamp = formattedDocument.expiryDate.getTime();

      if (startTimestamp >= expiryTimestamp) {
        tempErrors.expiryDate =
          "Expiry Date must be greater than Starting Date";
        hasError = true;
      }
    }

    // Update errors state
    setErrors(tempErrors);

    // Validate form fields with Joi schema
    const joiValidation = validateForm(
      formattedDocument,
      (joiErrors) => {
        tempErrors = { ...tempErrors, ...joiErrors };
        setErrors(tempErrors);
        if (Object.values(joiErrors).some((error) => error !== "")) {
          hasError = true;
        }
      },
      validationSchema,
      false
    );

    // Submit if no errors
    if (!hasError && joiValidation) {
      try {
        const result = await Confirm(formattedDocument, file);
        if (result === false) return;
      } catch {
        return;
      }
      setDocument({ ...emptyDocument });
      setFile(null);
      setFileName("");
      setEmailInput("");
      setEmailError("");
      setErrors({
        documentName: "",
        emails: "",
        startingDate: "",
        expiryDate: "",
      });
    }
  };

  return (
    <GenericPopup
      icon={<NoteAddRoundedIcon color="white" fontSize="large" />}
      open={showPopup}
      title={`${isEditing ? "Edit" : "Add"} ${entityLabel} Document`}
      content={content}
      buttons={[
        {
          text: "Cancel",
          onClick: onCancel,
          color: "secondary",
          variant: "outlined",
        },
        {
          text: isEditing ? "Save" : "Add",
          onClick: submit,
          color: "primary",
          variant: "contained",
          loading: loading,
        },
      ]}
    />
  );
};
